import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as Sentry from "../../lib/sentry";

// Auth API responses are untyped (ApiInstance calls have no generics), and
// consumers across the app rely on loose property access (uid, consumer_id,
// displayName, full_name, ...). This intentionally mirrors the previous
// implicit-any behavior of the (broken) circular alias it replaces.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserRecord = Record<string, any>;

interface AuthState {
  isAuthenticated: boolean;
  user: UserRecord | null;
  accessToken: string | null;
  hasExited: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  hasExited: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    registerSuccess: (
      state,
      action: PayloadAction<{ user: UserRecord; accessToken: string }>
    ) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      Sentry.setUser({ id: action.payload.user.id, email: action.payload.user.email });
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: UserRecord;
        accessToken: string;
      }>
    ) => {
      const { user, accessToken } = action.payload;
      state.isAuthenticated = true;
      state.user = user;
      state.accessToken = accessToken;
      Sentry.setUser({ id: user.id, email: user.email });
    },
    updateAccessToken: (
      state,
      action: PayloadAction<string | null>
    ) => {
      if (action.payload) {
        state.isAuthenticated = true;
        state.accessToken = action.payload;
      } else {
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
      }
    },
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      Sentry.setUser(null);
    },
    // Clears only the token when a refresh fails — keeps user/isAuthenticated
    // intact so the UI doesn't flash to the logged-out state. The next API
    // call will trigger another refresh attempt or the user can re-login.
    sessionExpired: (state) => {
      state.accessToken = null;
    },
    setHasExited: (state, action) => {
      state.hasExited = action.payload;
    },
    updateUserData: (state, action: PayloadAction<UserRecord>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const {
  registerSuccess,
  loginSuccess,
  logoutSuccess,
  updateAccessToken,
  sessionExpired,
  setHasExited,
  updateUserData,
} = authSlice.actions;

export default authSlice.reducer;
